<template>
  <div id="content" class="app-content">
    <!-- page header -->
    <b-row>
      <b-col>
        <h1 class="page-header">CSV Codes Container <small>all listings.</small></h1>
      </b-col>

      <b-col sm="auto" class="ml-auto">
        <b-button :disabled="$cannot('supplier.manage')" v-b-modal.newCSVCodeContainer> New Container </b-button>

        <b-modal
          title="New Code Container"
          id="newCSVCodeContainer"
          hide-footer
          v-slot:default="{ close }"
          :no-close-on-backdrop="true"
        >
          <csv-codes-container-form @close="refresh(close)" />
        </b-modal>
      </b-col>
    </b-row>

    <!-- page content -->
    <b-card>
      <b-table :fields="fields" :items="listing" show-empty stacked="sm" bordered hover id="csv-codes-container-table">
        <template #cell(product_variation)="data">
          <span v-html="data.value"></span>
        </template>
        <template #cell(supplier)="data">
          <span v-html="data.value"></span>
        </template>
        <template #cell(place)="data">
          <span v-html="data.value"></span>
        </template>
        <template #cell(status)="{ item }">
          <b-badge :variant="containerStateVariant(item)">
            {{ containerState(item) }}
          </b-badge>
        </template>
        <template v-slot:cell(actions)="row">
            <b-button class="btn-block" variant="default" size="sm" v-b-modal="`editCSVCodeContainer-${row.item.id}`"> View </b-button>
            <b-modal title="Edit Container Details" :id="`editCSVCodeContainer-${row.item.id}`" hide-footer no-close-on-backdrop v-slot:default="{ close }">
                <csv-codes-container-edit-form :container="row" @close="refresh(close)" />
            </b-modal>

            <div v-if="containerState(row.item) !== 'Deleted'">
                <b-button
                  class="btn-block"
                  :variant="row.item.status ? 'warning' : 'primary'"
                  size="sm"
                  :disabled="$cannot('supplier.manage') || statusUpdatingIds.length > 0"
                  @click="updateStatus(row.item, !row.item.status)"
                >
                  {{ row.item.status ? 'Deactivate' : 'Activate' }}
                </b-button>

                <b-button class="btn-block" variant="danger" size="sm" v-b-modal="`deleteCSVCodeContainer-${row.item.id}`"> Delete </b-button>
                <b-modal :id="`deleteCSVCodeContainer-${row.item.id}`" hide-footer no-close-on-backdrop>
                    <validation-observer v-slot="{ invalid }">
                        <form @submit.prevent="deleteContainer(row.item.id)">
                            <h5 v-if="row.item.used_codes > 0" class="text-center">
                                This container carries used codes ({{ row.item.used_codes }} codes), are you sure you want to delete this container #{{ row.item.id }}?
                            </h5>
                            <h5 v-else class="text-center">Are you sure you want to delete container: #{{ row.item.id }}?</h5>
                            <b-row align-h="center">
                                <b-col cols="auto">
                                    <b-button type="submit" variant="danger" :disabled="$cannot('supplier.manage') || invalid"> Delete </b-button>
                                </b-col>
                            </b-row>
                        </form>
                    </validation-observer>
                </b-modal>
            </div>
        </template>
      </b-table>

      <!-- pagination -->
      <b-pagination
        v-model="meta.current_page"
        :total-rows="meta.total"
        :per-page="meta.per_page"
        @change="paginationChange"
      />
    </b-card>
  </div>
</template>

<script>
import { containers } from '@/api'
import CsvCodesContainerForm from './CSVCodesContainerForm'
import CsvCodesContainerEditForm from './CSVCodesContainerEditForm'
import {
  activateWithConflicts,
  buildContainerPayload,
  conflictMessage,
  containerErrorMessage,
  findOverlappingActiveContainers,
  getContainerState,
  loadAllContainers,
} from './csv-container-status'

export default {
  name: 'CSVCodes',
  inject: ['$cannot'],
  components: { CsvCodesContainerForm, CsvCodesContainerEditForm },
  data() {
    return { listing: [], meta: {}, statusUpdatingIds: [] }
  },
  computed: {
    fields() {
      return [
        { key: 'id', label: 'ID' },
        {
          key: 'product_id',
          label: 'Product ID',
        },
        {
          key: 'supplier',
          label: 'Supplier',
          formatter: (value, key, item) => {
            return this.generateSupplier(item)
          },
        },
        {
          key: 'product_variation',
          label: 'Variation',
          formatter: (value, key, item) => {
            return this.generateVariationNames(item)
          },
        },
        {
          key: 'place',
          label: 'Place',
          formatter: (value, key, item) => {
            return this.generatePlaces(item)
          },
        },
        { key: 'valid_for' },
        { key: 'status', label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ]
    },
  },

  beforeRouteEnter(from, to, next) {
    next((vm) => vm.loadContent())
  },

  methods: {
    async loadContent() {
      const page = this.$route.query.page ?? 1
      const response = await containers.list(page)

      this.$nextTick(() => {
        this.listing = response.data()
        this.meta = response.meta
      })
    },

    paginationChange(page) {
      this.$router.push(page === 1 ? { name: 'csv-codes-containers' } : { name: 'csv-codes-containers', query: { page } })

      this.loadContent()
    },

    refresh(callback) {
      this.loadContent()
      callback()
    },

    generateVariationNames(item) {
      let variation_names = ''
      item.product_variations.forEach(product_variation => {
        variation_names += product_variation['variation_name'] + '<br>'
      })
      return variation_names
    },

    generateSupplier(item) {
      return item.supplier.booking_name
    },

    generatePlaces(item) {
      let places = ''
      item.product_variations.forEach(product_variation => {
        places += (product_variation.place_name ?? 'No place') + '<br>'
      })
      return places
    },

    containerState(item) {
      const state = getContainerState(item)
      return state.charAt(0).toUpperCase() + state.slice(1)
    },

    containerStateVariant(item) {
      return {
        active: 'success',
        inactive: 'warning',
        deleted: 'secondary',
      }[getContainerState(item)]
    },

    async confirmReplacement(conflicts, variations) {
      if (!conflicts.length) {
        return true
      }

      return this.$bvModal.msgBoxConfirm(conflictMessage(conflicts, variations), {
        title: 'Replace active containers?',
        okTitle: 'Deactivate and activate',
        okVariant: 'warning',
        cancelTitle: 'Cancel',
      })
    },

    async updateStatus(item, status) {
      const previousStatus = item.status
      this.statusUpdatingIds.push(item.id)

      try {
        await this.$store.dispatch('auth/refreshToken')

        if (!status) {
          this.$set(item, 'status', false)
          await containers.update(item.id, buildContainerPayload(item, false))
        } else {
          const allContainers = await loadAllContainers(containers)
          const target = allContainers.find((container) => container.id === item.id) ?? item
          const conflicts = findOverlappingActiveContainers(
            allContainers,
            target.product_variations,
            target.id,
          )

          if (!await this.confirmReplacement(conflicts, target.product_variations)) {
            return
          }

          await activateWithConflicts(containers, target, conflicts)
        }

        this.$bvToast.toast(`Container ${status ? 'activated' : 'deactivated'}.`, {
          title: 'Container status updated',
          variant: 'primary',
        })
      } catch (error) {
        this.$set(item, 'status', previousStatus)
        const progress = error.containerStatusProgress
        const deactivatedIds = progress?.deactivatedIds ?? []
        const partial = progress
          ? `${deactivatedIds.length ? ` Containers ${deactivatedIds.map((id) => `#${id}`).join(', ')} were deactivated.` : ''} Container #${item.id} was not activated.`
          : ''

        this.$bvToast.toast(`${containerErrorMessage(error)}${partial}`, {
          title: progress ? 'Container replacement incomplete' : 'Container status update failed',
          variant: 'danger',
        })
      } finally {
        this.statusUpdatingIds = this.statusUpdatingIds.filter((id) => id !== item.id)
        await this.loadContent()
      }
    },

    async deleteContainer(id) {
        await this.$store.dispatch('auth/refreshToken');
        await containers.destroy(id)

        this.loadContent()

        this.$bvToast.toast(`Container deleted.`, {
            title: 'Container deleted',
            variant: 'primary',
        })
        this.$bvModal.hide("deleteCSVCodeContainer-" + id)

        this.$emit('close')
    }
  },
}
</script>
